import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../api/supabase';
import { useAuth } from '../../context/AuthContext';
import { postJournal } from '../../api/journal';
import { ACCOUNTS } from '../../api/accounts';
import { getInvoicePaidAmount } from '../../api/receiptAllocations';
import { downloadCsv } from '../../utils/downloadCsv';

export default function CommissionLedger() {
  const { user } = useAuth();
  const username = user?.email?.split('@')[0] || 'unknown';
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState('PENDING');
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [agents, setAgents] = useState([]);
  const [agentFilter, setAgentFilter] = useState('');

  useEffect(() => {
    supabase.from('agents').select('id, tls_code, full_name').eq('is_active', true).order('tls_code').then(({ data }) => setAgents(data || []));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    let q = supabase.from('commissions').select('*, agents:agent_ref_id(tls_code, full_name)').order('created_at', { ascending: false });
    if (status) q = q.eq('commission_status', status);
    const { data } = await q;
    const list = data || [];

    // reference_id is polymorphic — for commissions saved from Costing Pending it's the
    // invoice's own id, but commissions saved at ticket/visa/booking creation time store the
    // source record's id instead (before an invoice necessarily exists). Match on both so the
    // ledger can always show the real invoice number and client, not just a raw UUID.
    const refIds = [...new Set(list.map(c => c.reference_id).filter(Boolean))];
    let invoiceByRef = {};
    if (refIds.length) {
      // With hundreds of commissions, cramming every unique id into one .or() blows past
      // PostgREST's URL length limit and silently 400s — same root cause as the URL-length
      // bugs already fixed elsewhere in this codebase. Chunk it.
      const CHUNK = 100;
      let invs = [];
      for (let i = 0; i < refIds.length; i += CHUNK) {
        const slice = refIds.slice(i, i + CHUNK);
        const { data } = await supabase.from('invoices')
          .select('id, invoice_number, issued_date, status, line_items, source_id, clients(full_name, client_code)')
          .or(`id.in.(${slice.join(',')}),source_id.in.(${slice.join(',')})`);
        if (data) invs = invs.concat(data);
      }
      // One getInvoicePaidAmount call per unique invoice (not per commission row — several
      // commissions can point at the same invoice), so the ledger can show whether the
      // money behind each commission has actually been collected, not just that it was
      // earned.
      const withPaid = await Promise.all((invs || []).map(async inv => {
        const total = (inv.line_items || []).reduce((s, li) => s + (parseFloat(li.unit_price ?? li.unitPrice ?? li.amount ?? 0) * parseFloat(li.qty ?? li.quantity ?? 1)), 0);
        const paid = inv.status === 'VOID' ? 0 : await getInvoicePaidAmount(inv.id);
        const balance = total - paid;
        const payStatus = inv.status === 'VOID' ? 'Void' : balance <= 0.01 ? 'Paid' : paid > 0.01 ? 'Partially Paid' : 'Unpaid';
        return { ...inv, total, paid, payStatus };
      }));
      withPaid.forEach(inv => {
        invoiceByRef[inv.id] = inv;
        if (inv.source_id) invoiceByRef[inv.source_id] = inv;
      });
    }

    setRows(list.map(c => ({ ...c, invoice: invoiceByRef[c.reference_id] })));
    setLoading(false);
  }, [status]);

  useEffect(() => { load(); }, [load]);

  // Some older flows (visa/ticket/booking creation forms) already post the commission
  // expense journal immediately at sale time — for those, approval is just a status flip.
  // The Costing Pending commission flow instead defers the journal until an admin actually
  // approves it here, so the liability and expense land in the period it was incurred (the
  // invoice date) without being posted before anyone reviewed the number.
  const approve = async (c) => {
    const { data: existing } = await supabase.from('journal_entries')
      .select('id').eq('reference_type', 'COMMISSION_APPROVAL').eq('reference_id', c.id).limit(1);
    if (!existing?.length && c.agent_ref_id && parseFloat(c.commission_amount) > 0) {
      const entryDate = c.invoice?.issued_date || c.created_at?.slice(0, 10) || new Date().toISOString().slice(0, 10);
      const commAmt = parseFloat(c.commission_amount);
      await postJournal({
        date: entryDate,
        description: `Agent commission: ${c.agents ? c.agents.full_name : 'agent'} — ${c.invoice?.invoice_number || c.product_type}`,
        reference_type: 'COMMISSION_APPROVAL',
        reference_id: c.id,
        exchange_rate: 1,
        lines: [
          { account_id: ACCOUNTS.COMMISSION_EXPENSE, debit_amount: commAmt, credit_amount: 0, description: 'Commission expense' },
          { account_id: ACCOUNTS.COMMISSION_PAYABLE, debit_amount: 0, credit_amount: commAmt, description: 'Commission payable', party_type: 'agent', party_id: c.agent_ref_id },
        ],
        created_by: username,
      });
    }
    await supabase.from('commissions').update({ commission_status: 'APPROVED', approved_at: new Date().toISOString() }).eq('id', c.id);
    load();
  };

  // A commission can be partly set off against what the agent owes (settled_amount) — only the
  // unsettled remainder is still pending/approved; the settled part counts as paid.
  const settledOf = (r) => parseFloat(r.settled_amount || 0);
  const owedOf = (r) => parseFloat(r.commission_amount || 0) - (r.commission_status === 'PAID' ? parseFloat(r.commission_amount || 0) : settledOf(r));
  const pending = rows.filter(r => r.commission_status === 'PENDING').reduce((s, r) => s + owedOf(r), 0);
  const approved = rows.filter(r => r.commission_status === 'APPROVED').reduce((s, r) => s + owedOf(r), 0);
  const paid = rows.reduce((s, r) => s + (r.commission_status === 'PAID' ? parseFloat(r.commission_amount || 0) : settledOf(r)), 0);

  // Lets an admin group "which commissions can actually be paid out" — a commission's
  // own PENDING/APPROVED/PAID status is about the commission itself, but whether the
  // client has actually SETTLED the underlying invoice is a separate, equally important
  // question before releasing money to the agent.
  const PAY_ORDER = { Unpaid: 0, 'Partially Paid': 1, Paid: 2, Void: 3 };
  const handleSort = (key) => { if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortKey(key); setSortDir('asc'); } };
  const filteredRows = agentFilter ? rows.filter(r => r.agent_ref_id === agentFilter) : rows;

  // "Fully paid" = the client has actually settled the invoice behind this commission —
  // regardless of the commission row's own PENDING/APPROVED/PAID status. This is the
  // set an admin actually cares about when deciding what's safe to approve/pay out next.
  const fullyPaidRows = filteredRows.filter(r => r.invoice?.payStatus === 'Paid');
  const fullyPaidInvoiceCount = new Set(fullyPaidRows.map(r => r.invoice.id)).size;
  const commissionDueOnPaid = fullyPaidRows.reduce((s, r) => s + parseFloat(r.commission_amount || 0), 0);
  // "Unpaid" here means anything still outstanding (Unpaid or Partially Paid) — the
  // mirror image of fully paid, so an admin can see at a glance how much commission is
  // riding on money the client hasn't actually handed over yet.
  const unpaidRows = filteredRows.filter(r => r.invoice?.payStatus === 'Unpaid' || r.invoice?.payStatus === 'Partially Paid');
  const unpaidInvoiceCount = new Set(unpaidRows.map(r => r.invoice.id)).size;
  const commissionDueOnUnpaid = unpaidRows.reduce((s, r) => s + parseFloat(r.commission_amount || 0), 0);
  const sortedRows = sortKey ? [...filteredRows].sort((a, b) => {
    const av = sortKey === 'payStatus' ? (PAY_ORDER[a.invoice?.payStatus] ?? -1) : a[sortKey];
    const bv = sortKey === 'payStatus' ? (PAY_ORDER[b.invoice?.payStatus] ?? -1) : b[sortKey];
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  }) : filteredRows;
  const sortArrow = (key) => sortKey === key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

  const handleExport = () => {
    if (!sortedRows.length) return;
    const rows = [
      ['Type', 'Agent Code', 'Agent Name', 'Invoice #', 'Client', 'Invoice Paid?', 'GP Amount', 'GP %', 'Commission %', 'Commission', 'Status'],
      ...sortedRows.map(c => [
        c.product_type,
        c.agents?.tls_code || '',
        c.agents?.full_name || '',
        c.invoice?.invoice_number || '',
        c.invoice?.clients?.full_name || '',
        c.invoice?.payStatus || '',
        parseFloat(c.gp_amount || 0).toFixed(2),
        parseFloat(c.gp_percent || 0).toFixed(1),
        c.commission_percent,
        parseFloat(c.commission_amount || 0).toFixed(2),
        c.commission_status,
      ]),
    ];
    const agentLabel = agentFilter ? (agents.find(a2 => a2.id === agentFilter)?.tls_code || 'agent') : 'all-agents';
    downloadCsv(rows, `agent-commissions-${agentLabel}-${(status || 'all').toLowerCase()}-${new Date().toISOString().slice(0, 10)}.csv`);
  };

  return (
    <div>
      <div className="page-header">
        <div><div className="page-title">Commission Ledger</div><div className="page-subtitle">Agent earnings — auto calculated on every sale</div></div>
        <button className="btn btn-secondary" onClick={handleExport}>Export CSV</button>
      </div>

      <div className="stats-grid" style={{ marginBottom: 20 }}>
        <div className="stat-card"><div className="stat-label">Pending</div><div className="stat-value amount">{pending.toLocaleString()}</div></div>
        <div className="stat-card"><div className="stat-label">Approved</div><div className="stat-value amount" style={{ color: 'var(--warning)' }}>{approved.toLocaleString()}</div></div>
        <div className="stat-card"><div className="stat-label">Paid</div><div className="stat-value amount" style={{ color: 'var(--success)' }}>{paid.toLocaleString()}</div></div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
        <div className="card" style={{ borderLeft: '3px solid #16a34a' }}>
          <div style={{ display: 'flex', gap: 32 }}>
            <div>
              <div className="stat-label">Fully Paid Invoices{agentFilter ? ' (this agent)' : ''}</div>
              <div className="stat-value" style={{ color: '#16a34a' }}>{fullyPaidInvoiceCount}</div>
            </div>
            <div>
              <div className="stat-label">Commission Due on Fully Paid</div>
              <div className="stat-value amount" style={{ color: '#16a34a' }}>{commissionDueOnPaid.toLocaleString()}</div>
            </div>
          </div>
        </div>
        <div className="card" style={{ borderLeft: '3px solid #dc2626' }}>
          <div style={{ display: 'flex', gap: 32 }}>
            <div>
              <div className="stat-label">Unpaid Invoices{agentFilter ? ' (this agent)' : ''}</div>
              <div className="stat-value" style={{ color: '#dc2626' }}>{unpaidInvoiceCount}</div>
            </div>
            <div>
              <div className="stat-label">Commission Due on Unpaid</div>
              <div className="stat-value amount" style={{ color: '#dc2626' }}>{commissionDueOnUnpaid.toLocaleString()}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
          {[['','All'],['PENDING','Pending'],['APPROVED','Approved'],['PAID','Paid']].map(([v,l]) => (
            <button key={v} className={`btn btn-sm ${status===v?'btn-primary':'btn-secondary'}`} onClick={() => setStatus(v)}>{l}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
          <button className={`btn btn-sm ${agentFilter===''?'btn-primary':'btn-secondary'}`} onClick={() => setAgentFilter('')}>All Agents</button>
          {agents.map(a => (
            <button key={a.id} className={`btn btn-sm ${agentFilter===a.id?'btn-primary':'btn-secondary'}`} onClick={() => setAgentFilter(a.id)}>{a.tls_code}</button>
          ))}
        </div>
        {loading ? <div className="spinner" /> : (
          <div className="table-wrapper">
            <table>
              <thead><tr><th>Type</th><th>Agent</th><th>Invoice #</th><th>Client</th><th style={{ cursor: 'pointer' }} onClick={() => handleSort('payStatus')}>Invoice Paid?{sortArrow('payStatus')}</th><th>GP Amount</th><th>GP %</th><th>Commission %</th><th>Commission</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {sortedRows.map(c => (
                  <tr key={c.id}
                    onClick={() => c.invoice && navigate(`/invoices/${c.invoice.id}`)}
                    style={{ cursor: c.invoice ? 'pointer' : 'default' }}
                    onMouseEnter={e => { if (c.invoice) e.currentTarget.style.background = 'var(--bg)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = ''; }}>
                    <td><span className="badge badge-blue">{c.product_type}</span></td>
                    <td>{c.agents ? `${c.agents.tls_code} — ${c.agents.full_name}` : '—'}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 600, color: c.invoice ? 'var(--primary)' : 'inherit' }}>{c.invoice?.invoice_number || '—'}</td>
                    <td>{c.invoice?.clients?.full_name || '—'}</td>
                    <td>
                      {c.invoice ? (
                        <span className={`badge ${c.invoice.payStatus === 'Paid' ? 'badge-green' : c.invoice.payStatus === 'Partially Paid' ? 'badge-yellow' : c.invoice.payStatus === 'Void' ? 'badge-red' : 'badge-gray'}`}>
                          {c.invoice.payStatus}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="amount">{parseFloat(c.gp_amount||0).toLocaleString()}</td>
                    <td>{parseFloat(c.gp_percent||0).toFixed(1)}%</td>
                    <td>{c.commission_percent}%</td>
                    <td className="amount" style={{ fontWeight: 700 }}>
                      {parseFloat(c.commission_amount||0).toLocaleString()}
                      {c.commission_status !== 'PAID' && parseFloat(c.settled_amount) > 0 && <div style={{ fontSize: 11, fontWeight: 400, color: 'var(--text-muted)' }}>{parseFloat(c.settled_amount).toLocaleString()} set off</div>}
                    </td>
                    <td><span className={`badge ${c.commission_status==='PAID'?'badge-green':c.commission_status==='APPROVED'?'badge-yellow':'badge-blue'}`}>{c.commission_status}</span></td>
                    <td>{c.commission_status==='PENDING' && <button className="btn btn-secondary btn-sm" onClick={e => { e.stopPropagation(); approve(c); }}>Approve</button>}</td>
                  </tr>
                ))}
                {sortedRows.length === 0 && <tr><td colSpan={11} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 16 }}>No commissions yet</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
